import { AlertTriangle, Check, Database, RefreshCw, Train } from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import { type AccessStatus, type PlatformAccess, useAccessStatus } from "@/lib/api";
import { cn } from "@/lib/utils";

interface PlatformAccessBannerProps {
  onStatusChange: (status: AccessStatus | null) => void;
}

interface PlatformRowProps {
  label: string;
  icon: React.ReactNode;
  access: PlatformAccess;
  verifiedMessage: string;
  deniedFallback: string;
  unknownFallback: string;
}

function PlatformRow({
  label,
  icon,
  access,
  verifiedMessage,
  deniedFallback,
  unknownFallback,
}: PlatformRowProps) {
  if (access.has_access === true) {
    return (
      <div className="flex items-center gap-2">
        <Check className="size-4 shrink-0 text-green-600 dark:text-green-400" />
        <span className="text-muted-foreground text-sm">{label}</span>
        <Check className="ml-auto size-4 text-green-600 dark:text-green-400" />
        <span className="sr-only">{verifiedMessage}</span>
      </div>
    );
  }

  if (access.has_access === false) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-destructive shrink-0">{icon}</span>
        <span className="text-destructive text-sm">{access.detail ?? deniedFallback}</span>
        <AlertTriangle className="text-destructive ml-auto size-4 shrink-0" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0 text-amber-600 dark:text-amber-400">{icon}</span>
      <span className="text-sm text-amber-800 dark:text-amber-200">
        {access.detail ?? unknownFallback}
      </span>
      <AlertTriangle className="ml-auto size-4 shrink-0 text-amber-600 dark:text-amber-400" />
    </div>
  );
}

// Fail open: render an explicit "couldn't verify" state instead of
// disappearing, so users keep visibility. A null has_access does not block
// project creation (the home page gate treats it as unknown).
const UNKNOWN_ACCESS: AccessStatus = {
  supabase: { has_access: null, detail: "Could not verify Supabase access." },
  railway: { has_access: null, detail: "Could not verify Railway access." },
};

export function PlatformAccessBanner({ onStatusChange }: PlatformAccessBannerProps) {
  const { data, isLoading: loading, isError, isFetching, refetch } = useAccessStatus();
  // Prefer last-known-good data: a transient background-refetch failure (30s
  // poll / focus refetch) should not flip confirmed rows to "couldn't verify".
  // Only fall back to UNKNOWN_ACCESS when there is genuinely no prior data.
  const status = data ?? (isError ? UNKNOWN_ACCESS : null);

  useEffect(() => {
    if (data !== undefined) {
      onStatusChange(data);
    } else if (isError) {
      onStatusChange(UNKNOWN_ACCESS);
    }
  }, [data, isError, onStatusChange]);

  if (loading) {
    return (
      <div className="border-border bg-card mt-3 rounded-lg border p-4">
        <p className="text-muted text-sm">Checking platform access...</p>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  const supabaseDenied = status.supabase.has_access === false;
  const railwayDenied = status.railway.has_access === false;
  const hasDenied = supabaseDenied || railwayDenied;
  // Anything other than a confirmed `true` on both platforms (denied or
  // unverified) means access isn't settled yet — offer a manual re-check
  // alongside the automatic 30s polling in `useAccessStatus`.
  const allVerified = status.supabase.has_access === true && status.railway.has_access === true;

  return (
    <div
      className={cn(
        "mt-3 rounded-lg border p-3",
        hasDenied
          ? "border-destructive/40 bg-destructive/5 dark:bg-destructive/10"
          : "border-border bg-card",
      )}
    >
      <div className="space-y-2">
        <PlatformRow
          label="Supabase access verified"
          icon={<Database className="size-4" />}
          access={status.supabase}
          verifiedMessage="Supabase access verified"
          deniedFallback="You are not a member of the Supabase organization. Contact Ruben for access."
          unknownFallback="Couldn't verify Supabase access."
        />
        <PlatformRow
          label="Railway access verified"
          icon={<Train className="size-4" />}
          access={status.railway}
          verifiedMessage="Railway access verified"
          deniedFallback="You are not a member of the Railway workspace. Contact Ruben for access."
          unknownFallback="Couldn't verify Railway access."
        />
      </div>
      {!allVerified && (
        <div className="mt-3 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={cn("mr-2 size-4", isFetching && "animate-spin")} />
            {isFetching ? "Checking..." : "Check again"}
          </Button>
        </div>
      )}
    </div>
  );
}
